import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';

import { Settlement, SettlementBasic, SettlementFor } from '~hooks/useSettlements/models';

import {
  payableInvoiceCategories,
  receivableInvoiceCategories,
} from '../../pages/Approvals/DriverPay/constants';

/**
 * Returns the account for a settlement based on the category
 * @param settlement Settlement instance
 * @param category Invoice category
 * @returns SettlementFor instance with the name and record
 */
export const getSettlementFor = (
  settlement: Settlement,
  category: InvoiceCategoryFilter | undefined,
): SettlementFor => {
  const { customerAccount, vendorAccount, driver } = settlement;

  let name = '';
  let record = null;

  // if there's no category, we return an empty SettlementFor instance
  if (!category) {
    return new SettlementFor(name, record);
  }

  if (payableInvoiceCategories.includes(category)) {
    name = vendorAccount?.name ?? driver?.fullName ?? '';
    record = vendorAccount ?? driver;
  } else {
    name = customerAccount?.name ?? '';
    record = customerAccount;
  }

  return new SettlementFor(name, record);
};

/**
 * Returns the friendly settlement ID based on the category context.
 * For Receivables: returns AR settlement ID (e.g., "AR-123")
 * For Payables: returns AP settlement ID (e.g., "AP-123")
 * Falls back to the original settlementId if friendly IDs are not available.
 *
 * @param settlement Settlement or SettlementBasic instance
 * @param category Invoice category filter
 * @returns The appropriate friendly settlement ID
 */
export const getFriendlySettlementId = (
  settlement: Settlement | SettlementBasic,
  category: InvoiceCategoryFilter | undefined,
): string => {
  // Receivables category → AR ID
  if (category && receivableInvoiceCategories.includes(category)) {
    return settlement.arSettlementId || settlement.settlementId;
  }

  // Payables category → AP ID (default)
  return settlement.apSettlementId || settlement.settlementId;
};

export const getSettlementCounterpartyName = (settlement: Settlement) =>
  settlement.customerAccount?.name ??
  settlement.vendorAccount?.name ??
  settlement.driver?.fullName ??
  '-';

// Negative totals show in parentheses, as in accounting statements.
export const formatSettlementTotal = (
  settlement: Settlement,
  currencyFormatter: (amount: number) => string,
) => {
  const totalAmount = settlement.rebillTotalAmount ?? settlement.totalAmount;

  return totalAmount >= 0
    ? currencyFormatter(totalAmount)
    : `(${currencyFormatter(-totalAmount)})`;
};
